import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { useItemCategoryData } from "../hooks/useItemCategoryData";
import { CategorySection1 } from "../components/CategorySection1";
import { useSearchParams } from "react-router-dom";
import {
  Home,
  Video,
  Star,
  User,
  BookOpen,
  FileText,
  Megaphone,
  List,
  Folder,
} from "lucide-react";
import SinglePage from "./SinglePage1";
import TreeBanner from "../components/TreeBanner";
import { Dropdown } from "../components/Dropdown";
import BreadcrumbBar from "../components/BreadcrumbBar";
import HinduTodayFooter from "../layouts/HinduTodayFooter";
import { findNodeByPath } from "../helper/findNodeByPath";
import Select from "react-select";

// Default filter selections
const DEFAULT_FILTERS = {
  country: "UK",
  language: "en",
  category: "gn",
};

const NODE_ICONS = {
  home: Home,
  video: Video,
  feature: Star,
  people: User,
  webstories: BookOpen,
  blog: FileText,
  classified: List,
  advertise: Megaphone,
};

const getNodeIcon = (name, depth) => {
  const Icon = NODE_ICONS[name] || Folder;
  return (
    <Icon
      size={depth === 0 ? 27 : 24}
      className="shrink-0 mx-auto opacity-90"
    />
  );
};

/* =========================
   MAIN COMPONENT
   ========================= */
export default function VedicKnowledgeTree() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [showTreeMobile, setShowTreeMobile] = useState(false);

  const [path, setPath] = useState([]);
  const countryParam = searchParams.get("country");
  const languageParam = searchParams.get("language");
  const categoryParam = searchParams.get("category");

  const [selectedCountry, setSelectedCountry] = useState(
    searchParams.get("country") || DEFAULT_FILTERS.country,
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    searchParams.get("language") || DEFAULT_FILTERS.language,
  );
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || DEFAULT_FILTERS.category,
  );

  const { data, isPending, error } = useQuery({
    queryKey: ["country", selectedCountry],
    queryFn: async () => {
      const res = await axios.get(
        "https://thehindutoday.com/api/v1/public/selectors",
        {
          params: {
            country_id: selectedCountry,
          },
        },
      );
      return res.data;
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 6, // 6 hours
  });

  const countries = data?.data?.countries ?? {};
  const languages = data?.data?.languages ?? {};
  const categories = data?.data?.categories ?? {};

  const [selectedArticle, setSelectedArticle] = useState(null);

  const [category_name, setCategory_name] = useState("");
  const [category_key, setCategory_key] = useState("");
  const [hasClicked, setHasClicked] = useState(false);
  const { data: categoryList, isPending: categoryPending } =
    useItemCategoryData({
      endpoint: "stories",
      category_name,
      selectedCategory,
      selectedCountry,
      selectedLanguage,
      enabled: category_key?.toLowerCase() === "feature",
    });

  const { data: personalityList, isPending: personalityPending } =
    useItemCategoryData({
      endpoint: "people",
      category_name,
      selectedCategory,
      selectedCountry,
      selectedLanguage,
      enabled: category_key?.toLowerCase() === "people",
    });
  const { data: videoList, isPending: videoPending } = useItemCategoryData({
    endpoint: "videos",
    category_name,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    enabled: category_key?.toLowerCase() === "video",
  });
  const { data: webList, isPending: webPending } = useItemCategoryData({
    endpoint: "web-stories",
    category_name,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    enabled: category_key?.toLowerCase() === "webstories",
  });
  const { data: blogList, isPending: blogPending } = useItemCategoryData({
    endpoint: "blogs",
    category_name,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    enabled: category_key?.toLowerCase() === "blog",
  });
  const { data: newsList, isPending: newsPending } = useItemCategoryData({
    endpoint: "news",
    category_name,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    enabled: category_key?.toLowerCase() === "news",
  });
  const { data: ebookList, isPending: ebookPending } = useItemCategoryData({
    endpoint: "ebooks",
    category_name,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    enabled: category_key?.toLowerCase() === "ebook",
  });
  const { data: magazineList, isPending: magazinePending } =
    useItemCategoryData({
      endpoint: "magazine",
      category_name,
      selectedCategory,
      selectedCountry,
      selectedLanguage,
      enabled: category_key?.toLowerCase() === "magazine",
    });
  // Fetch menu dynamically
  const {
    data: menuData,
    isLoading: loadingTree,
    error: treeError,
  } = useQuery({
    queryKey: ["menuData", selectedCountry, selectedLanguage, selectedCategory],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);

      const url = `https://thehindutoday.com/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
    onError: (err) => console.warn("Menu fetch error:", err),
  });

  const { data: footerData } = useQuery({
    queryKey: [
      "hindu_footer",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);

      const url = `https://thehindutoday.com/api/v1/public/footer?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
    onError: (err) => console.warn("Menu fetch error:", err),
  });

  const mapMenuToTree = (items = []) => {
    return items
      ?.filter((item) => item?.key !== "home")
      .map((item) => ({
        name: item.label,
        key: item.key,
        url: item.path ? `https://thehindutoday.com${item.path}` : null,
        attributes: {
          type: "detail",
          description: item.code || item.key || "",
        },
        children: item.children?.length ? mapMenuToTree(item.children) : null,
      }));
  };

  // Transform API response into tree structure
  const treeData = useMemo(() => {
    if (!menuData?.data?.items) return null;

    return {
      name: "Home",
      key: "home",
      attributes: {
        type: "root",
        description: "Dynamic Menu",
      },
      children: mapMenuToTree(menuData.data.items),
    };
  }, [menuData]);

  const updateParam = (key, value, resetTree = true) => {
    setPath([]);
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    if (resetTree) {
      params.delete("path");
      setPath([]);
    }
    setSearchParams(params);
  };

  const sections = [
    {
      key: "feature",
      data: categoryList?.data?.stories,
      loading: categoryPending,
    },
    {
      key: "people",
      data: personalityList?.data?.personality,
      loading: personalityPending,
    },
    { key: "video", data: videoList?.data?.videos, loading: videoPending },
    {
      key: "webstories",
      data: webList?.data?.web_stories,
      loading: webPending,
    },
    { key: "blog", data: blogList?.data?.blogs, loading: blogPending },
    { key: "news", data: newsList?.data?.news, loading: newsPending },
    { key: "ebook", data: ebookList?.data?.ebooks, loading: ebookPending },
    {
      key: "magazine",
      data: magazineList?.data?.magazine,
      loading: magazinePending,
    },
  ];

  const activeSection = sections.find(
    (sec) => sec.key === category_key?.toLowerCase(),
  );

  const [showNodeMobile, setShowNodeMobile] = useState(false);

  const { data: FeatureData, isError } = useQuery({
    queryKey: [
      "FeatureData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      params.append("is_featured", 1);
      params.append("per_page", 3);

      const res = await axios.get(
        `https://thehindutoday.com/api/v1/public/people?${params.toString()}`,
      );
      return res.data;
    },

    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const { data: BlogsData } = useQuery({
    queryKey: [
      "BlogsData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);

      params.append("is_featured", 1);
      params.append("per_page", 3);

      const res = await axios.get(
        `https://thehindutoday.com/api/v1/public/blogs?${params.toString()}`,
      );
      return res.data;
    },

    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const { data: VideoData } = useQuery({
    queryKey: [
      "VideoData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);

      params.append("is_featured", 1);
      params.append("per_page", 4);

      const res = await axios.get(
        `https://thehindutoday.com/api/v1/public/videos?${params.toString()}`,
      );
      return res.data;
    },

    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const { data: StoriesData } = useQuery({
    queryKey: [
      "StoriesData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      params.append("is_featured", 1);
      params.append("per_page", 3);

      const res = await axios.get(
        `https://thehindutoday.com/api/v1/public/stories?${params.toString()}`,
      );
      return res.data;
    },

    retry: false,
    staleTime: 1000 * 60 * 60,
  });
  const { data: newsHomeData } = useQuery({
    queryKey: [
      "newsHomeData",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      params.append("is_featured", 1);
      params.append("per_page", 3);

      const res = await axios.get(
        `https://thehindutoday.com/api/v1/public/news?${params.toString()}`,
      );
      return res.data;
    },

    retry: false,
    staleTime: 1000 * 60 * 60,
  });

  const withPosition = (arr) =>
    (Array.isArray(arr) ? arr : []).map((item, i) => ({
      ...item,
      position: i,
    }));

  const [navList, setNavList] = useState([]);

  const openArticle = (list, article) => {
    const listWithPos = withPosition(list);

    // If article already has position, keep it; else derive from list
    const idx =
      typeof article?.position === "number"
        ? article.position
        : listWithPos.findIndex(
            (x) => x?.id === article?.id || x?.slug === article?.slug,
          );

    setNavList(listWithPos);
    setSelectedArticle({
      ...article,
      position: idx >= 0 ? idx : 0,
    });

    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (treeData && treeData.name && path.length === 0) {
      setPath([treeData.name]); // auto-expand root
    }
  }, [treeData, path]);

  useEffect(() => {
    if (hasClicked) return;

    setCategory_key("");
    setCategory_name("");

    ["stories", "people", "videos", "web-stories", "blog"].forEach((key) => {
      queryClient.removeQueries({ queryKey: [key] });
    });
  }, [path, hasClicked, queryClient]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathParam = params.get("path");

    if (pathParam && treeData) {
      const parts = pathParam.split(">");
      setPath([treeData.name, ...parts]);
    }
  }, [treeData]);

  useEffect(() => {
    if (countryParam) setSelectedCountry(countryParam);
    if (languageParam) setSelectedLanguage(languageParam);
    if (categoryParam) setSelectedCategory(categoryParam);
  }, [categoryParam, countryParam, languageParam, searchParams]); // 👈 ONLY first time

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    let changed = false;

    if (!params.get("country")) {
      params.set("country", DEFAULT_FILTERS.country);
      changed = true;
    }

    if (!params.get("language")) {
      params.set("language", DEFAULT_FILTERS.language);
      changed = true;
    }

    if (!params.get("category")) {
      params.set("category", DEFAULT_FILTERS.category);
      changed = true;
    }

    if (changed) {
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const updateUrlWithPath = (newPath) => {
    const params = new URLSearchParams(window.location.search);
    const childPath = newPath.slice(1); // remove "Home"

    if (childPath.length === 0) params.delete("path");
    else params.set("path", childPath.join(">"));

    const newUrl =
      params.toString().length > 0
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

    window.history.pushState({}, "", newUrl);
  };

  console.log(path);

  const onChipClick = (child) => {
    setHasClicked(false);

    // new path = current path + child
    const newPath = [...path.slice(0, path.length), child.name];
    setPath(newPath);
    updateUrlWithPath(newPath);

    const hasChildren = child?.children?.length > 0;

    if (!hasChildren) {
      // leaf => load list under that node
      setHasClicked(true);
      setCategory_name(child.name);
      setCategory_key(child.key);
      setSelectedArticle(null);

      // mobile UX: close tree after selection
      if (window.innerWidth < 768) setShowTreeMobile(false);
    } else {
      // branch => just expand, reset list view
      setSelectedArticle(null);
      setCategory_key("");
      setCategory_name("");
    }
  };

  const activeNode = useMemo(() => {
    if (!treeData) return null;
    return findNodeByPath(treeData, path);
  }, [treeData, path]);

  const activeChildren = useMemo(() => {
    return activeNode?.children?.length ? activeNode.children : [];
  }, [activeNode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // F12
      if (e.key === "F12") {
        e.preventDefault();
      }

      // Ctrl + Shift + I / J / C
      if (e.ctrlKey && e.shiftKey && ["I", "J", "C"].includes(e.key)) {
        e.preventDefault();
      }

      // Ctrl + U (view source)
      if (e.ctrlKey && e.key.toLowerCase() === "u") {
        e.preventDefault();
      }

      // Ctrl + S (save)
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
      }
    };

    // Right click disable
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);
  const mapOptions = (data, fallback) => {
    if (!data || !Object.keys(data).length) return [fallback];

    return Object.entries(data).map(([value, label]) => ({
      value,
      label,
    }));
  };

  const categoryOptions = mapOptions(categories, {
    value: "gn",
    label: "General",
  });

  const countryOptions = mapOptions(countries, {
    value: "UK",
    label: "United Kingdom",
  });

  const languageOptions = mapOptions(languages, {
    value: "en",
    label: "English",
  });
  return (
    <>
      <div
        className="min-h-screen bg-gray-100  relative the-hindu  select-none"
        onContextMenu={(e) => e.preventDefault()}
        onCopy={(e) => e.preventDefault()}
        onPaste={(e) => e.preventDefault()}
        onCut={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
      >
        <div className="pb-10 pt-2 px-3">
          {/* Filter Dropdowns */}
          <div className=" flex justify-between items-center flex-wrap mb-2">
            <div className=" hidden lg:block"></div>

            <div className=" z-50 grid grid-cols-3 md:flex  md:flex-wrap justify-end gap-2 max-sm:mb-2 max-lg:w-full">
              <Select
                classNamePrefix="custom-select"
                options={countryOptions}
                value={countryOptions.find((c) => c.value === selectedCountry)}
                onChange={(val) => {
                  const country = val?.value || "";
                  setSelectedCountry(country);
                  updateParam("country", country);
                }}
                placeholder="Country"
                isSearchable
                styles={{
                  container: (base) => ({
                    ...base,
                    // minWidth: "197px",
                    marginTop: "2px",
                  }),
                }}
              />

              {/* Language */}
              <Select
                classNamePrefix="custom-select"
                options={languageOptions}
                value={languageOptions.find(
                  (l) => l.value === selectedLanguage,
                )}
                onChange={(val) => {
                  const language = val?.value || "";
                  setSelectedLanguage(language);
                  updateParam("language", language);
                  setHasClicked(false);
                }}
                placeholder="Language"
                isSearchable
                styles={{
                  container: (base) => ({
                    ...base,
                    // minWidth: "150px",
                    marginTop: "2px",
                  }),
                }}
              />

              {/* Category */}
              <Select
                classNamePrefix="custom-select"
                options={categoryOptions}
                value={categoryOptions.find(
                  (c) => c.value === selectedCategory,
                )}
                onChange={(val) => {
                  const category = val?.value || "";
                  setSelectedCategory(category);
                  updateParam("category", category);
                  setHasClicked(false);
                }}
                placeholder="Category"
                isSearchable
                styles={{
                  container: (base) => ({
                    ...base,
                    // minWidth: "100px",
                    marginTop: "2px",
                  }),
                }}
              />
            </div>
          </div>

          {/* Breadcrumb 🤣*/}
          <div className="flex items-center flex-wrap justify-between gap-2 mb-2">
            <BreadcrumbBar
              path={selectedArticle ? [...path, selectedArticle?.title] : path}
              setPath={setPath}
            />

            {/* Mobile Toggle Button */}
            {showTreeMobile === false && (
              <button
                onClick={() => setShowTreeMobile(true)}
                className="
      md:hidden
      px-3 py-1.5
      rounded-md
      bg-indigo-600
      text-white
      text-sm
      font-semibold
      shadow
      ml-auto
    "
              >
                {showTreeMobile ? "☰" : "☰"}
              </button>
            )}
            {showNodeMobile && (
              <div className="md:hidden px-3 py-2">
                <button
                  onClick={() => {
                    setSelectedArticle(null);
                    setCategory_key("");
                    setCategory_name("");
                    [
                      "stories",
                      "people",
                      "videos",
                      "web-stories",
                      "blog",
                    ].forEach((key) => {
                      queryClient.removeQueries({ queryKey: [key] });
                    });
                  }}
                  className="
                          w-full
                          px-4 py-2
                          rounded-lg
                          bg-indigo-600
                          text-white
                          font-semibold
                          text-sm
                          shadow
                          ml-auto
                        "
                >
                  ☰
                </button>
              </div>
            )}
          </div>

          {/* Loading / Error */}
          {loadingTree && (
            <div className="flex justify-center items-center py-8">
              <p className="text-gray-500 text-lg">Loading menu...</p>
            </div>
          )}

          {treeError && (
            <div className="flex justify-center items-center py-8">
              <p className="text-red-500 text-lg">
                Failed to load menu. Please try again later.
              </p>
            </div>
          )}

          {/* TREE SCROLL CONTAINER */}
          {!loadingTree && treeData && (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 px-0 md:px-4">
              {/* LEFT: Category Section (80%) 🤣*/}
              <div className="md:col-span-8">
                {!activeSection && !showTreeMobile && !selectedArticle && (
                  <div className="block">
                    <TreeBanner
                      selectedCategory={selectedCategory}
                      selectedCountry={selectedCountry}
                      selectedLanguage={selectedLanguage}
                    />
                  </div>
                )}

                <div className=" block md:hidden">
                  {(showTreeMobile || showNodeMobile) && (
                    <div className="px-6 md:px-0 py-3 tree-scroll">
                      <TreeNode
                        node={treeData}
                        path={path}
                        setPath={setPath}
                        depth={0}
                        setCategory_name={setCategory_name}
                        setCategory_key={setCategory_key}
                        setSelectedArticle={setSelectedArticle}
                        setHasClicked={setHasClicked}
                        setShowTreeMobile={setShowTreeMobile} // 👈 pass this
                        setShowNodeMobile={setShowNodeMobile}
                      />
                    </div>
                  )}
                </div>

                {selectedArticle ? (
                  <SinglePage
                    article={selectedArticle}
                    onBack={() => {
                      setSelectedArticle(null);
                      setHasClicked(true);
                      window.scrollTo(0, 0);
                    }}
                    onPrev={() => {
                      if (!navList?.length || selectedArticle?.position == null)
                        return;

                      const prevIndex =
                        selectedArticle.position > 0
                          ? selectedArticle.position - 1
                          : navList.length - 1;

                      setSelectedArticle({
                        ...navList[prevIndex],
                        position: prevIndex,
                      });
                      window.scrollTo(0, 0);
                    }}
                    onNext={() => {
                      if (!navList?.length || selectedArticle?.position == null)
                        return;

                      const nextIndex =
                        selectedArticle.position < navList.length - 1
                          ? selectedArticle.position + 1
                          : 0;

                      setSelectedArticle({
                        ...navList[nextIndex],
                        position: nextIndex,
                      });
                      window.scrollTo(0, 0);
                    }}
                  />
                ) : (
                  <>
                    {!activeSection && (
                      <div className="block">
                        <div className="">
                          {/* Section Heading */}
                          {VideoData?.data?.videos?.length > 0 && (
                            <>
                              <div className="text-center my-6 sm:my-10">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                                  {menuData?.data?.items[2]?.label}
                                </h2>
                                <div className="w-20 h-1 bg-orange-500 mx-auto mt-3 rounded-full"></div>
                              </div>
                              <CategorySection1
                                // loading={activeSection.loading}
                                title={category_name}
                                category_key={category_key}
                                data={VideoData?.data?.videos}
                                hasClicked={hasClicked}
                                variant="slider"
                                onArticleClick={(article) =>
                                  openArticle(VideoData?.data?.videos, article)
                                }
                              />
                            </>
                          )}
                          {StoriesData?.data?.stories?.length > 0 && (
                            <>
                              <div className="text-center my-6 sm:my-10">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                                  {menuData?.data?.items[3]?.label}
                                </h2>
                                <div className="w-20 h-1 bg-orange-500 mx-auto mt-3 rounded-full"></div>
                              </div>
                              <CategorySection1
                                // loading={activeSection.loading}
                                title={category_name}
                                category_key={category_key}
                                data={StoriesData?.data?.stories}
                                hasClicked={hasClicked}
                                onArticleClick={(article) =>
                                  openArticle(
                                    StoriesData?.data?.stories,
                                    article,
                                  )
                                }
                              />
                            </>
                          )}
                          {FeatureData?.data?.personality?.length > 0 && (
                            <>
                              <div className="text-center my-6 sm:my-10">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                                  {menuData?.data?.items[5]?.label}
                                </h2>
                                <div className="w-20 h-1 bg-orange-500 mx-auto mt-3 rounded-full"></div>
                              </div>

                              <CategorySection1
                                title={category_name}
                                category_key={category_key}
                                data={FeatureData?.data?.personality}
                                hasClicked={hasClicked}
                                onArticleClick={(article) =>
                                  openArticle(
                                    FeatureData?.data?.personality,
                                    article,
                                  )
                                }
                              />
                            </>
                          )}

                          {newsHomeData?.data?.news?.length > 0 && (
                            <>
                              <div className="text-center my-6 sm:my-10">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                                  {menuData?.data?.items[4]?.label}
                                </h2>
                                <div className="w-20 h-1 bg-orange-500 mx-auto mt-3 rounded-full"></div>
                              </div>

                              <CategorySection1
                                title={category_name}
                                category_key={category_key}
                                data={newsHomeData?.data?.news}
                                hasClicked={hasClicked}
                                onArticleClick={(article) =>
                                  openArticle(newsHomeData?.data?.news, article)
                                }
                              />
                            </>
                          )}

                          {BlogsData?.data?.blogs?.length > 0 && (
                            <>
                              <div className="text-center my-6 sm:my-10">
                                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800">
                                  {menuData?.data?.items[8]?.label}
                                </h2>
                                <div className="w-20 h-1 bg-orange-500 mx-auto mt-3 rounded-full"></div>
                              </div>
                              <CategorySection1
                                // loading={activeSection.loading}
                                title={category_name}
                                category_key={category_key}
                                data={BlogsData?.data?.blogs}
                                hasClicked={hasClicked}
                                onArticleClick={(article) =>
                                  openArticle(BlogsData?.data?.blogs, article)
                                }
                              />
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {activeSection && (
                      <CategorySection1
                        loading={activeSection.loading}
                        title={category_name}
                        category_key={category_key}
                        data={activeSection.data}
                        hasClicked={hasClicked}
                        onArticleClick={(article) =>
                          openArticle(activeSection.data, article)
                        }
                      />
                    )}
                  </>
                )}
              </div>

              {/* RIGHT: Tree (20%) */}
              <div className="md:col-span-4 md:min-h-screen max-md:hidden">
                {/* {showTreeMobile && ( */}
                <div className="px-6 md:px-0 py-3 tree-scroll hidden md:block">
                  <TreeNode
                    node={treeData}
                    path={path}
                    setPath={setPath}
                    depth={0}
                    setCategory_name={setCategory_name}
                    setCategory_key={setCategory_key}
                    setSelectedArticle={setSelectedArticle}
                    setHasClicked={setHasClicked}
                    setShowTreeMobile={setShowTreeMobile} // 👈 pass this
                  />
                </div>
                {/* )} */}
              </div>
            </div>
          )}
        </div>
        <HinduTodayFooter data={footerData} />
      </div>
    </>
  );
}

const colors = {
  root: "bg-[#881b21] text-white",
  branch: "bg-[#ff7d13] text-white",
  detail: "bg-[#c94f2d] text-white",
};

const getNodeColor = (node, depth) => {
  if (depth === 0) return colors.root; // Root
  if (node.children && node.children.length > 0)
    // Parent
    return colors.branch;
  return colors.detail; // Leaf
};

//
function TreeNode({
  node,
  path,
  setPath,
  depth,
  setCategory_name,
  setCategory_key,
  setSelectedArticle,
  setHasClicked,
  setShowTreeMobile,
  setShowNodeMobile,
}) {
  const isExpanded = path[depth] === node.name;
  const hasChildren = node.children && node.children.length > 0;
  const isLeaf = !hasChildren;

  const updateUrlWithPath = (newPath) => {
    const params = new URLSearchParams(window.location.search);

    const childPath = newPath.slice(1); // root hatao

    if (childPath.length === 0) {
      params.delete("path");
    } else {
      params.set("path", childPath.join(">"));
    }

    const newUrl =
      params.toString().length > 0
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;

    window.history.pushState({}, "", newUrl);
  };

  const toggleNode = (e) => {
    e.stopPropagation();
    if (!hasChildren) return;

    setHasClicked(false); // 👈 important

    setPath((prev) => {
      let newPath;

      if (prev[depth] === node.name) {
        // 🔽 collapse → upar ke level tak
        newPath = prev.slice(0, depth);
      } else {
        // 🔼 expand → yahan tak ka path
        newPath = [...prev.slice(0, depth), node.name];
      }

      updateUrlWithPath(newPath); // ✅ ALWAYS sync URL
      return newPath;
    });
  };

  const openLink = (e) => {
    if (node.url) {
      // if (hasChildren && node.url) {
      e.stopPropagation(); // 🚨 important
      window.open(node.url, "_blank");
    }
  };
  const openListEvent = (e) => {
    e.stopPropagation();

    setHasClicked(true);
    setCategory_name(node.name);
    setCategory_key(node.key);
    setSelectedArticle(null);

    const newPath = [...path.slice(0, depth), node.name];
    setPath(newPath);
    updateUrlWithPath(newPath);
    // 📱 Mobile: tree hide
    if (window.innerWidth < 768 && node.key !== "home") {
      setTimeout(() => {
        setShowTreeMobile(false);
      }, 200); // ⏱ small delay 🤣
    }
    if (window.innerWidth < 768 && node.key === "home") {
      setShowTreeMobile(false);
    }
  };

  return (
    <div className="flex flex-col items-center relative">
      {/* NODE */}
      <motion.div
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.96 }}
        // onClick={() => {
        //   if (isLeaf && node.url) window.open(node.url, "_blank");
        // }}
        className={`
          ${isLeaf ? "cursor-pointer" : "cursor-default"}
          px-4 py-2
          min-w-[97px]
          rounded-2xl
          shadow-md hover:shadow-xl
          bg-gradient-to-r ${getNodeColor(node, depth)}
          border border-white/20
          backdrop-blur-sm
           sm:w-auto max-w-md
          transition-all duration-200
          ${hasChildren ? "ring-1 ring-white/10" : ""}
        `}
      >
        {/* TITLE + ICONS */}
        <div className="block mx-auto items-center justify-between gap-3">
          {/* TITLE (non-clickable) */}
          <span
            className="flex-block mx-auto justify-center items-center gap-2 font-semibold cursor-pointer text-sm sm:text-[13px]"
            title={node.name}
            onClick={(e) => openListEvent(e)}
          >
            {getNodeIcon(node.key, depth)}
            <p className="text-[12px] text-center truncate">{node.name}</p>
          </span>

          <div className="flex justify-center items-center gap-1">
            {/* EXTERNAL LINK ICON */}
            {node.url && (
              <span
                onClick={openLink}
                className="   opacity-70
    shrink-0
    cursor-pointer
    transition-transform
    duration-200
    hover:scale-125
  "
              >
                <ExternalLink size={14} />
              </span>
            )}

            {/* ARROW ICON */}
            {hasChildren && (
              <span
                onClick={toggleNode}
                className="opacity-80 shrink-0     cursor-pointer
                        transition-transform
                        duration-200
                        hover:scale-125"
              >
                {isExpanded ? (
                  <ChevronDown size={20} />
                ) : (
                  <ChevronRight size={20} />
                )}
              </span>
            )}
          </div>
        </div>
      </motion.div>

      {/* CONNECTOR */}
      {hasChildren && isExpanded && (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 24 }}
          className="w-px bg-gradient-to-b from-gray-400 to-transparent my-3"
        />
      )}

      {/* CHILDREN */}
      <AnimatePresence>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex flex-row flex-wrap items-center justify-center gap-4"
          >
            {node.children
              .filter(
                (c) =>
                  path[depth + 1] === undefined || path[depth + 1] === c.name,
              )
              .map((child) => (
                <TreeNode
                  key={child.name}
                  node={child}
                  path={path}
                  setPath={setPath}
                  depth={depth + 1}
                  setSelectedArticle={setSelectedArticle}
                  setCategory_name={setCategory_name}
                  setCategory_key={setCategory_key}
                  setHasClicked={setHasClicked}
                  setShowTreeMobile={setShowTreeMobile}
                  setShowNodeMobile={setShowNodeMobile}
                />
              ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
